import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ThumbsUp, ThumbsDown, Flag, MessageSquarePlus, User } from 'lucide-react';
import { useVisitTips } from '@/hooks/useVisitTips';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from '@/hooks/useTranslation';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

interface VisitTipsSectionProps {
  placeName: string;
}

export default function VisitTipsSection({ placeName }: VisitTipsSectionProps) {
  const { language } = useTranslation();
  const { user } = useAuth();
  const { tips, loading, addTip, toggleReaction, reportTip } = useVisitTips(placeName, language);
  const navigate = useNavigate();
  
  const [newTipContent, setNewTipContent] = useState('');
  const [isAddingTip, setIsAddingTip] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [reportReason, setReportReason] = useState('');

  const handleAddTip = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    setIsAddingTip(true);
    const success = await addTip(newTipContent);
    if (success) {
      setNewTipContent('');
      setShowAddForm(false);
    }
    setIsAddingTip(false);
  };

  const handleReaction = (tipId: string, type: 'like' | 'dislike') => {
    if (!user) {
      navigate('/auth');
      return;
    }
    toggleReaction(tipId, type);
  };

  const handleReport = async (tipId: string) => {
    if (reportReason) {
      await reportTip(tipId, reportReason);
      setReportReason('');
    }
  };

  const getDisplayName = (tip: any) => {
    return tip.profiles?.full_name || 'Utilisateur anonyme';
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
            <MessageSquarePlus className="w-5 h-5" />
            {language === 'FR' ? 'Conseils de la communauté' : 'Community Tips'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-lg">
          <div className="flex items-center gap-2">
            <MessageSquarePlus className="w-5 h-5" />
            {language === 'FR' ? 'Conseils de la communauté' : 'Community Tips'}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => user ? setShowAddForm(!showAddForm) : navigate('/auth')}
          >
            {language === 'FR' ? 'Ajouter un conseil' : 'Add a tip'}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {showAddForm && (
          <Card className="border-primary/20">
            <CardContent className="pt-4">
              <div className="space-y-3">
                <Textarea
                  placeholder={language === 'FR' 
                    ? 'Partagez votre expérience et vos conseils pour ce lieu...'
                    : 'Share your experience and tips for this place...'
                  }
                  value={newTipContent}
                  onChange={(e) => setNewTipContent(e.target.value)}
                  className="min-h-[100px]"
                />
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowAddForm(false);
                      setNewTipContent('');
                    }}
                  >
                    {language === 'FR' ? 'Annuler' : 'Cancel'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleAddTip}
                    disabled={isAddingTip || newTipContent.trim().length < 10}
                  >
                    {isAddingTip 
                      ? (language === 'FR' ? 'Ajout...' : 'Adding...') 
                      : (language === 'FR' ? 'Publier' : 'Publish')
                    }
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}


        {tips.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            <p>
              {language === 'FR' 
                ? user 
                  ? 'Aucun conseil pour ce lieu. Soyez le premier à partager votre expérience !'
                  : 'Aucun conseil pour ce lieu pour le moment.'
                : user
                  ? 'No tips for this place yet. Be the first to share your experience!'
                  : 'No tips for this place yet.'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {tips.map((tip) => (
                <Card key={tip.id} className="border-l-4 border-l-primary/30">
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    {/* Header with user info and actions */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={tip.profiles?.avatar_url || ''} />
                          <AvatarFallback>
                            {tip.profiles?.full_name ? (
                              getInitials(tip.profiles.full_name)
                            ) : (
                              <User className="w-4 h-4" />
                            )}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">
                            {getDisplayName(tip)}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(tip.created_at), { 
                              addSuffix: true,
                              locale: language === 'FR' ? fr : undefined
                            })}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-8 gap-1 ${
                            user && tip.likes_user_ids.includes(user.id) 
                              ? 'text-green-600 bg-green-50 hover:bg-green-100' 
                              : 'text-muted-foreground'
                          }`}
                          onClick={() => handleReaction(tip.id, 'like')}
                        >
                          <ThumbsUp className="w-3 h-3" />
                          <span>{tip.likes_user_ids.length}</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-8 gap-1 ${
                            user && tip.dislikes_user_ids.includes(user.id) 
                              ? 'text-red-600 bg-red-50 hover:bg-red-100' 
                              : 'text-muted-foreground'
                          }`}
                          onClick={() => handleReaction(tip.id, 'dislike')}
                        >
                          <ThumbsDown className="w-3 h-3" />
                          <span>{tip.dislikes_user_ids.length}</span>
                        </Button>
                        {user && user.id !== tip.user_id && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600 hover:bg-red-50"
                                title={language === 'FR' ? 'Signaler ce conseil' : 'Report this tip'}
                              >
                                <Flag className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  {language === 'FR' ? 'Signaler ce conseil' : 'Report this tip'}
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  {language === 'FR' 
                                    ? 'Pourquoi souhaitez-vous signaler ce conseil ?'
                                    : 'Why do you want to report this tip?'
                                  }
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <Select value={reportReason} onValueChange={setReportReason}>
                                <SelectTrigger>
                                  <SelectValue placeholder={
                                    language === 'FR' ? 'Choisir une raison' : 'Choose a reason'
                                  } />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="spam">
                                    {language === 'FR' ? 'Spam' : 'Spam'}
                                  </SelectItem>
                                  <SelectItem value="inappropriate">
                                    {language === 'FR' ? 'Contenu inapproprié' : 'Inappropriate content'}
                                  </SelectItem>
                                  <SelectItem value="false_info">
                                    {language === 'FR' ? 'Fausses informations' : 'False information'}
                                  </SelectItem>
                                  <SelectItem value="off_topic">
                                    {language === 'FR' ? 'Hors sujet' : 'Off topic'}
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <AlertDialogFooter>
                                <AlertDialogCancel onClick={() => setReportReason('')}>
                                  {language === 'FR' ? 'Annuler' : 'Cancel'}
                                </AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleReport(tip.id)}
                                  disabled={!reportReason}
                                >
                                  {language === 'FR' ? 'Signaler' : 'Report'}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </div>
                    
                    {/* Content spanning full width */}
                    <p className="text-sm leading-relaxed w-full">{tip.content}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}